import type { Catalog, ILocale } from '@api/types';
import type { IOption } from '@shared/form/types';

import { apiSlice } from '@api/apiSlice';
import { catchCommonError } from '@utils/catchCommonError';
import { responseToSelectOptions } from '@utils/responseFormatters';

import { API } from '@constants';

export const systemValuesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEnums: builder.query<Catalog, unknown>({
      serializeQueryArgs: () => 'getEnums',
      query: () => ({
        url: API.CATALOG.ENUMS,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled.catch(({ error }) => {
          catchCommonError(error);
        });
      },
    }),
    getTimeZones: builder.query<IOption[], unknown>({
      serializeQueryArgs: () => 'getTimeZones',
      query: () => ({
        url: API.CATALOG.TIME_ZONES,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled.catch(({ error }) => {
          catchCommonError(error);
        });
      },
    }),
    getCountries: builder.query<IOption[], unknown>({
      serializeQueryArgs: () => 'getCountries',
      query: () => ({
        url: API.CATALOG.COUNTRIES,
      }),
      transformResponse: (response: Array<{ id: string; name: string }>) =>
        responseToSelectOptions(response, {
          key: 'name',
        }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled.catch(({ error }) => {
          catchCommonError(error);
        });
      },
    }),
    getCurrencies: builder.query<IOption[], unknown>({
      serializeQueryArgs: () => 'getCurrencies',
      query: () => ({
        url: API.CATALOG.CURRENCIES,
      }),
      transformResponse: (response: Array<{ id: string; code: string }>) =>
        responseToSelectOptions(response, {
          key: 'code',
        }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled.catch(({ error }) => {
          catchCommonError(error);
        });
      },
    }),
    getLocales: builder.query<IOption[], unknown>({
      serializeQueryArgs: () => 'getLocales',
      query: () => ({
        url: API.CATALOG.LOCALES,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        await queryFulfilled.catch(({ error }) => {
          catchCommonError(error);
        });
      },
      transformResponse: (response: ILocale[]) =>
        responseToSelectOptions(response, {
          key: 'displayName',
          withTranslation: true,
        }),
    }),
  }),
});

export const {
  useGetEnumsQuery,
  useGetLocalesQuery,
  useGetTimeZonesQuery,
  useGetCountriesQuery,
  useGetCurrenciesQuery,
} = systemValuesApiSlice;
